/**
 * Bounded report layout helpers. Definitions, enums and limits come from the server
 * options so the client never invents its own column schema; the server revalidates
 * every submitted layout.
 */

const METADATA_BLOCK = "metadata";

export const MARGIN_FIELDS = [
  { key: "marginLeftMm", label: "Kiri" },
  { key: "marginRightMm", label: "Kanan" },
  { key: "marginTopMm", label: "Atas" },
  { key: "marginBottomMm", label: "Bawah" },
];

/** Sheet size in millimetres for the layout's paper and orientation, or null if unknown. */
export const getPageSizeMm = (layout, options) => {
  const paper = options?.layoutOptions.paperDimensionsMm?.find(
    (entry) => entry.paper === layout?.paper
  );
  if (!paper) return null;
  return layout.orientation === "landscape"
    ? { width: paper.long, height: paper.short }
    : { width: paper.short, height: paper.long };
};

const LAYOUT_KEYS = [
  "fontFamily",
  "fontSize",
  "density",
  "headerTheme",
  "boldHeaders",
  "striped",
  "moneyDecimals",
  "negativeFormat",
  "orientation",
  "paper",
  "marginTopMm",
  "marginRightMm",
  "marginBottomMm",
  "marginLeftMm",
  "flexibleHeight",
  "title",
  "footerNote",
];

const hasControlCharacters = (value) =>
  [...value].some((character) => {
    const code = character.codePointAt(0);
    return code <= 0x1f || (code >= 0x7f && code <= 0x9f);
  });

/** Copies a layout, keeping only known fields. */
export const cloneLayout = (layout) => ({
  columns: layout.columns.map(({ id, visible, label, width }) => ({
    id,
    visible,
    label,
    width,
  })),
  columnOrder: [...layout.columnOrder],
  ...Object.fromEntries(LAYOUT_KEYS.map((key) => [key, layout[key]])),
});

const definitionsById = (options) =>
  new Map(options.columns.map((definition) => [definition.id, definition]));

export const getOrderedColumns = (layout, options) => {
  const definitions = definitionsById(options);
  const columns = new Map(layout.columns.map((column) => [column.id, column]));
  return layout.columnOrder.map((id) => ({
    definition: definitions.get(id),
    column: columns.get(id),
  }));
};

export const updateLayoutColumn = (layout, id, changes) => {
  const next = cloneLayout(layout);
  next.columns = next.columns.map((column) =>
    column.id === id ? { ...column, ...changes } : column
  );
  return next;
};

/** A column moves one step and only inside its own block. */
export const canMoveColumn = (layout, options, id, direction) => {
  const definitions = definitionsById(options);
  const index = layout.columnOrder.indexOf(id);
  const target = index + (direction === "up" ? -1 : 1);
  if (index < 0 || target < 0 || target >= layout.columnOrder.length) {
    return false;
  }
  return (
    definitions.get(layout.columnOrder[target])?.block ===
    definitions.get(id)?.block
  );
};

export const moveLayoutColumn = (layout, options, id, direction) => {
  if (!canMoveColumn(layout, options, id, direction)) return layout;
  const next = cloneLayout(layout);
  const index = next.columnOrder.indexOf(id);
  const target = index + (direction === "up" ? -1 : 1);
  [next.columnOrder[index], next.columnOrder[target]] = [
    next.columnOrder[target],
    next.columnOrder[index],
  ];
  return next;
};

const isIntegerWithin = (value, { min, max }) =>
  Number.isInteger(value) && value >= min && value <= max;

const validateText = (value, maxLength, { required = false } = {}) => {
  if (typeof value !== "string") return "Nilai tidak valid.";
  if (required && !value.trim()) return "Wajib diisi.";
  if (value.length > maxLength) return `Maksimal ${maxLength} karakter.`;
  if (hasControlCharacters(value)) return "Memuat karakter tidak valid.";
  return null;
};

/**
 * Returns a map of field keys to messages; an empty map means the layout is valid.
 * Mirrors the server rules: complete column set, in-block ordering, identity column
 * and at least one metric visible, and every bound from the options payload.
 */
export const validateLayout = (layout, options) => {
  const errors = {};
  if (!options || !layout || typeof layout !== "object") {
    return { general: "Tampilan tidak valid." };
  }

  const definitions = definitionsById(options);
  const canonicalOrder = options.defaultLayout.columnOrder;
  const bounds = options.layoutOptions;

  const columnIds = Array.isArray(layout.columns)
    ? layout.columns.map((column) => column?.id)
    : [];
  const order = Array.isArray(layout.columnOrder) ? layout.columnOrder : [];
  const isCompleteSet = (ids) =>
    ids.length === canonicalOrder.length &&
    new Set(ids).size === ids.length &&
    canonicalOrder.every((id) => ids.includes(id));

  if (!isCompleteSet(columnIds) || !isCompleteSet(order)) {
    return { general: "Daftar kolom tidak lengkap atau tidak dikenal." };
  }

  if (
    !order.every(
      (id, index) =>
        definitions.get(id).block ===
        definitions.get(canonicalOrder[index]).block
    )
  ) {
    errors.general = "Urutan kolom hanya dapat diubah di dalam kelompoknya.";
  }

  for (const column of layout.columns) {
    const definition = definitions.get(column.id);
    if (typeof column.visible !== "boolean") {
      errors.general = "Pengaturan kolom tidak valid.";
    }
    const labelError = validateText(column.label, bounds.labelMaxLength, {
      required: true,
    });
    if (labelError) errors[`columns.${column.id}.label`] = labelError;
    if (
      !isIntegerWithin(column.width, {
        min: definition.minWidth,
        max: definition.maxWidth,
      })
    ) {
      errors[`columns.${column.id}.width`] =
        `Lebar ${definition.minWidth}–${definition.maxWidth} mm.`;
    }
  }

  const isVisible = (id) =>
    layout.columns.find((column) => column.id === id)?.visible === true;
  if (!isVisible("name") && !isVisible("sku")) {
    errors.general = "Nama Barang atau Kode Barang harus ditampilkan.";
  }
  const metricIds = options.columns
    .filter((definition) => definition.block !== METADATA_BLOCK)
    .map((definition) => definition.id);
  if (!metricIds.some(isVisible)) {
    errors.general = "Minimal satu kolom kuantitas atau nilai harus ditampilkan.";
  }

  const enumChecks = [
    ["fontFamily", bounds.fontFamilies],
    ["density", bounds.densities],
    ["headerTheme", bounds.headerThemes],
    ["moneyDecimals", bounds.moneyDecimals],
    ["negativeFormat", bounds.negativeFormats],
    ["paper", bounds.paperSizes],
    ["orientation", bounds.orientations],
  ];
  for (const [key, allowed] of enumChecks) {
    if (!allowed.includes(layout[key])) errors[key] = "Pilihan tidak dikenal.";
  }
  for (const key of ["boldHeaders", "striped", "flexibleHeight"]) {
    if (typeof layout[key] !== "boolean") errors[key] = "Pilihan tidak valid.";
  }
  if (!isIntegerWithin(layout.fontSize, bounds.fontSize)) {
    errors.fontSize = `Ukuran ${bounds.fontSize.min}–${bounds.fontSize.max} pt.`;
  }
  for (const { key } of MARGIN_FIELDS) {
    if (!isIntegerWithin(layout[key], bounds.marginMm)) {
      errors[key] = `Margin ${bounds.marginMm.min}–${bounds.marginMm.max} mm.`;
    }
  }
  const titleError = validateText(layout.title, bounds.titleMaxLength);
  if (titleError) errors.title = titleError;
  const footerError = validateText(layout.footerNote, bounds.footerNoteMaxLength);
  if (footerError) errors.footerNote = footerError;

  return errors;
};

/** Full layout payload; column IDs travel as values, never as object keys. */
export const toLayoutRequest = (layout) => {
  const request = cloneLayout(layout);
  return {
    ...request,
    columns: request.columns.map((column) => ({
      ...column,
      label: column.label.trim(),
    })),
    title: request.title.trim(),
    footerNote: request.footerNote.trim(),
  };
};
